import Helper from '@ember/component/helper';
import { service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';

type Positional = [number | string | null | undefined];
type Named = { defaultValue?: string; hideCents?: boolean };

export interface FormatCentsToDollarsSignature {
  Args: {
    Positional: Positional;
    Named: Named;
  };
  Return: string | null;
}

/**
 * Takes a number or string that represents a dollar amount in cents and formats it as a dollar amount.
 * Optional accepts a default value to return if the value is null or undefined.
 *
 * {{format-cents-to-dollars @currentBalance defaultValue="N/A"}}
 *
 * `hideCents=true` drops the fractional part entirely ($5,250 rather than
 * $5,250.00) for small captions where the cents are noise. The cents are
 * truncated, never rounded up, so a displayed ceiling (e.g. "eligible up to")
 * can never read higher than the real amount.
 *
 * {{format-cents-to-dollars @annualLimit hideCents=true}}
 *
 */
export default class FormatCentsToDollars extends Helper<FormatCentsToDollarsSignature> {
  @service declare intl: IntlService;

  compute([valueToFormat]: Positional, named: Named = {}) {
    const defaultVal = named.defaultValue || '';

    if (valueToFormat === null || valueToFormat === undefined) {
      return defaultVal;
    }

    const valueInDollars =
      (typeof valueToFormat === 'number' ? valueToFormat : parseFloat(valueToFormat)) / 100;

    if (isNaN(valueInDollars)) {
      return defaultVal;
    }
    const fractionDigits = named.hideCents ? 0 : 2;
    // Truncate toward zero rather than letting Intl round the cents up.
    const valueToDisplay = named.hideCents ? Math.trunc(valueInDollars) : valueInDollars;

    try {
      return this.intl.formatNumber(valueToDisplay, {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      });
    } catch (e) {
      console.warn(`Error formatting ${valueToFormat} to dollars: ${e}`);
    }
    return defaultVal;
  }
}
